import { useState } from 'react';
import { useLocale, useQuery, type Option } from 'shared-ui';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import { useModalState } from 'hooks/useModalState/useModalState';
import { fetchServers } from 'api/servers/servers';
import { QueryKeyEnum } from 'core/global.enum';
import { fetchTerminals } from 'api/terminals/terminals';
import { GroupTerminalsPermissionsIdSortEnum } from 'context/query/queryContext/QueryContext.enum';
import { AppMessages } from 'i18n/messages';
import { downloadEventsReport } from 'api/reports/reports';
import { EventsReportInputStateEnum } from 'api/reports/reports.enum';
import { EventModeEnum } from 'api/events/events.enum';
import { useReportDownload } from '../hooks/useReportDownload';
import { useSmtpConfiguration } from '../hooks/useSmtpConfiguration';
import { EmailSelectionModalContainer } from '../emailSelectionModal';

import { useEventsReportValidation } from './EventsReport.validation';
import { EventsReportForm } from './EventsReport.types';
import { EventsReport } from './EventsReport';

export const EventsReportContainer = () => {
  const { isModalOpen, onModalClose, onModalOpen } = useModalState();
  const {
    isModalOpen: isEmailModalOpen,
    onModalClose: onEmailModalClose,
    onModalOpen: onEmailModalOpen,
  } = useModalState();
  const { formatMessage } = useLocale();
  const [selectedEmails, setSelectedEmails] = useState<Option[]>([]);
  const { isSmtpConfigured } = useSmtpConfiguration();

  const { schema } = useEventsReportValidation();
  const formMethods = useForm<EventsReportForm>({
    resolver: yupResolver(schema),
    mode: 'all',
  });

  const { komorki, tkds, serwerId } = formMethods.watch();

  const { data: serversResponse } = useQuery([QueryKeyEnum.SERVERS], fetchServers);
  const servers = serversResponse?.data || [];

  const { data: terminalsResponse, isInitialLoading: isLoadingTerminals } = useQuery(
    [QueryKeyEnum.TERMINALS, serwerId],
    () => fetchTerminals(serwerId),
    {
      args: {
        sort: [GroupTerminalsPermissionsIdSortEnum.ASC],
      },
      enabled: !!serwerId,
    },
  );
  const terminals = terminalsResponse?.data || [];

  const {
    onDownload: onDownloadEventsReport,
    isLoading: isLoadingDownload,
    hasNoDataError,
    resetError,
  } = useReportDownload({
    mutationFn: downloadEventsReport,
    buildRequestBody: (emails) => {
      const formValues = formMethods.watch();
      return {
        nazwisko: formValues.nazwisko,
        numerKarty: formValues.numerKarty,
        nrEwidencyjny: formValues.nrEwidencyjny,
        dataOd: formValues.dataOd?.toISOString().split('T')[0],
        dataDo: formValues.dataDo?.toISOString().split('T')[0],
        tryb: formValues.tryb as unknown as EventModeEnum,
        stanWejscia: formValues.stanWejscia as unknown as EventsReportInputStateEnum,
        komorka: komorki?.id ? [komorki?.id] : [],
        tkds: tkds?.map((tkd) => tkd.value.toString()),
        selectedEmails: emails,
      };
    },
    onEmailModalClose,
    reportNameId: AppMessages['reports.eventsReport'],
  });

  const handleEmailModalOpen = () => {
    resetError();
    onEmailModalOpen();
  };

  const handleModalOpen = () => {
    formMethods.reset();
    resetError();
    setSelectedEmails([]);
    onModalOpen();
  };

  const handleModalClose = () => {
    formMethods.reset();
    resetError();
    setSelectedEmails([]);
    onModalClose();
  };

  const handleConfirmEmails = (emails: string[]) => {
    // Save selected emails for persistence
    setSelectedEmails(emails.map((email) => ({ value: email, label: email })));
    // Trigger the download/send
    onDownloadEventsReport(emails);
  };

  return (
    <FormProvider {...formMethods}>
      <EventsReport
        servers={servers}
        isModalOpen={isModalOpen}
        onModalClose={handleModalClose}
        onModalOpen={handleModalOpen}
        terminals={terminals}
        isLoadingTerminals={isLoadingTerminals}
        onDownload={onDownloadEventsReport}
        isLoadingDownload={isLoadingDownload}
        hasNoDataError={hasNoDataError}
        onEmailModalOpen={handleEmailModalOpen}
        isEmailModalOpen={isEmailModalOpen}
        isSmtpConfigured={isSmtpConfigured}
        onResetError={resetError}
      />
      {isModalOpen && (
        <EmailSelectionModalContainer
          title={formatMessage({ id: AppMessages['reports.eventsReport'] })}
          open={isEmailModalOpen}
          onClose={onEmailModalClose}
          onConfirm={handleConfirmEmails}
          hasNoDataError={hasNoDataError}
          onResetError={resetError}
          selectedUsers={selectedEmails}
        />
      )}
    </FormProvider>
  );
};
